import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { StickyContainer } from 'react-sticky';
import qs from 'qs';

import { useAppContext } from '../../context';
import { isMobileOrTablet } from '../../utils/browserDetection';

import { routerContextShape } from '../../proptypes';
import { getObjFromHashParamString } from '../../utils/urls';
import { SEGMENT_EVENTS, trackUserInteraction } from '../../utils/tracking';

import { ModalContext } from '../../shared/Modals/modalContext';
import Footer from '../Footer';
import Header from '../Header';
import Meta from '../Meta';
import { getLocaleForUrlSlug } from '../../data/locale';
import { getPreferredLocale } from '../../utils/locale';
import { shouldRenderChangeLocaleBanner } from './helpers';
import CookiePolicyBanner from '../../shared/CookiePolicyBanner';

import TopBanner from './TopBanner';
import LocaleChange from './TopBanner/LocaleChange';
import BannerWrapper from './TopBanner/BannerWrapper';

import { vendBannerForLocale } from './bannerFactory';

import { main, mainWithoutHeader, whiteBg } from './styles.module.css';

/**
 * Page layout presentational component, including header and footer.
 */
class Layout extends Component {
  constructor(props) {
    super(props);

    this.presentModal = (modalId, name) => {
      if (this.state.currentlyDisplayedModal !== undefined) return false;
      this.setState({
        currentlyDisplayedModal: modalId,
      });
      trackUserInteraction(SEGMENT_EVENTS.PANEL_OPENED, {
        shortName: modalId,
        source: __CLIENT__ && window.location && window.location.pathname,
        name,
      });
      return true;
    };

    this.removeModal = (modalId) => {
      if (this.state.currentlyDisplayedModal === modalId) {
        this.setState({
          currentlyDisplayedModal: undefined,
        });

        trackUserInteraction(SEGMENT_EVENTS.PANEL_CLOSED, {
          shortName: modalId,
          source: __CLIENT__ && window.location && window.location.pathname,
        });

        return true;
      }
      return false;
    };

    this.state = {
      currentlyDisplayedModal: undefined,
      // eslint-disable-next-line react/no-unused-state
      presentModal: this.presentModal,
      // eslint-disable-next-line react/no-unused-state
      removeModal: this.removeModal,
    };
  }

  render() {
    const props = this.props;
    const {
      isMobile,
      children,
      location,
      routes,
      userSelectedLocale,
      countryCode,
      partnerSession,
    } = this.props;

    // Some routes have additional props applied by the routes configuration
    // The last element of the routes array is the final 'resolved' route
    const routeProps = routes && routes.slice(-1).pop();

    const {
      showExtendedFooter = false,
      noFooter,
      whiteBackground,
      noBanner,
      canUsePartnerTheme,
      useDiscoveryHeader,
    } = routeProps || {};

    /**
     * localeInUrl may come form two places:
     * 1. URL: /gb/running-events
     * 2. locale prop on <Route> declaration
     */
    let localeInUrl;
    if (routeProps && routeProps.locale) {
      localeInUrl = routeProps.locale;
    } else if (props.params && props.params.localeSlug) {
      localeInUrl = getLocaleForUrlSlug(props.params.localeSlug);

      if (localeInUrl) {
        localeInUrl = localeInUrl.iso2;
      }
    }

    const selectedLocale = getPreferredLocale({
      userSelectedLocale,
      localeInUrl,
    });

    let BannerComponent = null;
    if (
      shouldRenderChangeLocaleBanner({
        pathname: location.pathname,
        userSelectedLocale,
        localeInUrl,
        userCountryCodeByIp: countryCode,
        source: getObjFromHashParamString(location.hash).source,
      })
    ) {
      BannerComponent = () => (
        <LocaleChange
          countryCode={countryCode}
          userSelectedLocale={userSelectedLocale}
        />
      );
    } else {
      BannerComponent = vendBannerForLocale(selectedLocale);
    }

    // Pages with full-screen contain `?map=true` in the querystring
    const searchQuery = qs.parse(location.search.substring(1));
    const mapParamInSearchQuery = Boolean(searchQuery.map) === true;

    const showHeader = !(isMobile && mapParamInSearchQuery);
    const showFooter =
      (!noFooter && !mapParamInSearchQuery) || showExtendedFooter;

    return (
      <ModalContext.Provider value={this.state}>
        <div className={whiteBackground ? whiteBg : null}>
          <Meta />
          <StickyContainer>
            <CookiePolicyBanner />
            {showHeader && (
              <>
                <BannerWrapper mapParamInSearchQuery={mapParamInSearchQuery}>
                  <TopBanner
                    Component={BannerComponent}
                    noBanner={noBanner}
                    onlyLoggedOut={false}
                  />
                </BannerWrapper>

                <Header
                  selectedLocale={selectedLocale}
                  location={location}
                  canUsePartnerTheme={canUsePartnerTheme}
                  partnerSession={partnerSession}
                  useDiscoveryHeader={useDiscoveryHeader}
                />
              </>
            )}
            <main className={showHeader ? main : mainWithoutHeader}>
              {children}
            </main>
            {showFooter && <Footer showExtendedFooter={showExtendedFooter} />}
          </StickyContainer>
        </div>
      </ModalContext.Provider>
    );
  }
}

Layout.propTypes = {
  /**
   * Children contents of main container within layout.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   * Routes context props, see proptypes.
   */
  routes: routerContextShape.routes,
};

Layout.defaultProps = {
  children: [],
  routes: undefined,
};

function LayoutWithContext(props) {
  const {
    COUNTRY_CODE: countryCode,
    USER_SELECTED_LOCALE: userSelectedLocale,
    PARTNER_SESSION: partnerSession,
    USER_AGENT: userAgent,
  } = useAppContext();

  const contextProps = {
    countryCode,
    userSelectedLocale,
    partnerSession,
    isMobile: isMobileOrTablet(userAgent),
  };

  return <Layout {...props} {...contextProps} />;
}

export default LayoutWithContext;
